<template>
  <div>
    <admin-wrapper
      :straight-title="additionalModuleName"
      :inputs="inputs"
      :moduleEnabler="moduleEnabler"
      @change="editSingleSettingHandler"
      :is-form-fetched="isFormFetched"
      @save="saveCard"
    >
      <template #hydrantsInspectionsFeatureCard.input>
        <draggable-lists-manage-card
          left-title="admin.featureCardInspection"
          :layer-id="hydrantsInspectionsLayerId"
          @update:hasAnyRequiredOnRightSide="hasAnyRequiredOnRightSide = $event"
          :has-edit-attributes="true"
        >
        </draggable-lists-manage-card>
      </template>
      <template #hydrantsLayerId.input="{ dataType }">
        <v-row no-gutters>
          <v-col cols="4">
            <data-input
              :items="hydrantsAvailableLayers"
              itemText="name"
              itemValue="id"
              :data-type="dataType"
              v-model="editingHydrantsLayerId"
              @change="handleEditHydrantsLayerId"
            />
          </v-col>
        </v-row>
      </template>
      <template #symbolization.input>
        <base-button
          @click="openSymbolizationDialog"
          translation-path="menu.changeSymbolization"
          :disabled="!hydrantsInspectionsLayerId || !hydrantsLayerId"
          :loading="isStyleDialogOpening"
        />
      </template>
    </admin-wrapper>
    <style-dialog
      v-if="isStyleDialogLoaded"
      :is-visible.sync="isStyleDialogVisible"
      :layer="currentLayerStyleEdit"
      :attributes="currentLayerStyleEditAttributes"
      :geomType="currentLayerStyleEditGeometryType"
      :loading="isStyleButtonLoading"
      @acceptStyle="setLayerStyle"
      scope="module"
      is-admin
    />
  </div>
</template>

<script>
import { call, get } from 'vuex-pathify';

import AdminWrapper from '@/components/AdminWrapper';
import DataInput from '@/components/DataInput';
import DraggableListsManageCard from '@/components/DraggableListsManageCard';

export default {
  name: 'HydrantsSettings',
  components: {
    AdminWrapper,
    DataInput,
    DraggableListsManageCard,
    StyleDialog: () => import('@/components/StyleDialog'),
  },
  data: () => ({
    isFormFetched: false,
    editingHydrantsLayerId: null,
    isStyleDialogOpening: false,
    isStyleDialogLoaded: false,
    isStyleDialogVisible: false,
    isStyleButtonLoading: false,
    currentLayerStyleEdit: undefined,
    currentLayerStyleEditAttributes: undefined,
    currentLayerStyleEditGeometryType: undefined,
  }),
  computed: {
    additionalModuleName: get('admin/additionalModules.HYDRANTS_INSPECTIONS.verbose_name'),
    currentLayerSchema: get('layers/currentLayerSchema'),
    hydrantsAvailableLayers: get('hydrants/hydrantsAvailableLayers'),
    hydrantsInspectionsDataSourceName: get('admin/modulesMapping@hydrants_inspections.inspections_datasource_name'),
    hydrantsInspectionsLayerId: get('admin/modulesMapping@hydrants_inspections.inspections_layer_id'),
    hydrantsInspectionsLayer: get('layers/layers@:hydrantsInspectionsLayerId'),
    hydrantsLayerId: get('hydrants/hydrantsLayerId'),
    isModuleEnabled: get('admin/settingsValues@hydrants_inspections_module_enabled'),
    moduleEnabler() {
      return {
        name: 'hydrants_inspections_module_enabled',
        straightText: this.additionalModuleName,
        subtitle: this.$i18n.t('admin.moduleEnablerSubtitle', { suffix: this.additionalModuleName }),
      };
    },
    inputs() {
      return [
        {
          name: 'hydrantsLayerId',
          translationPath: 'admin.hydrantsLayerId',
          subtitle: 'admin.hydrantsLayerIdSubtitle',
          align: 'start',
          dataType: {
            name: 'select',
          },
        },
        {
          name: 'hydrants_inspections_module_next_inspection_threshold',
          translationPath: 'admin.hydrantsInspectionsModuleIextInspectioTthreshold',
          subtitle: 'admin.hydrantsInspectionsModuleIextInspectioTthresholdSubtitle',
          align: 'start',
          dataType: {
            name: 'integer',
          },
          minValue: 1,
        },
        {
          name: 'hydrants_inspections_module_next_inspection_soon_threshold',
          translationPath: 'admin.hydrantsInspectionsNextInspectionWarning',
          align: 'start',
          dataType: {
            name: 'integer',
          },
          subtitle: 'admin.hydrantsInspectionsNextInspectionWarningSubtitle',
        },
        {
          name: 'symbolization',
          translationPath: 'admin.defaultSymbolization',
          subtitle: 'admin.hydrantsInspectionsSymbolizationSubtitle',
          align: 'start',
          isCustom: true,
        },
        {
          name: 'hydrantsInspectionsFeatureCard',
          translationPath: 'admin.featureCardInspection',
          isCustom: true,
          subtitle: this.$i18n.t('admin.setFeatureCard', { type: this.$i18n.t('admin.inspectionType') }),
          align: 'start',
        },
      ];
    },
  },
  methods: {
    editHydrantsLayerId: call('hydrants/editHydrantsLayerId'),
    editLayer: call('layers/editLayer'),
    editSingleSetting: call('admin/editSingleSetting'),
    getDataSourceForm: call('layers/getDataSourceForm'),
    getFeaturesLayer: call('layers/getFeaturesLayer'),
    getHydrantsAvailableLayers: call('hydrants/getHydrantsAvailableLayers'),
    getHydrantsLayerId: call('hydrants/getHydrantsLayerId'),
    getLayerMetadata: call('layers/getLayerMetadata'),
    getModulesMapping: call('admin/getModulesMapping'),
    editSingleSettingHandler(value, name) {
      if (name === 'hydrants_inspections_module_next_inspection_soon_threshold') {
        this.editSingleSetting({
          key: name,
          body: {
            value,
          },
        });
      } else {
        return;
      }
    },
    async handleEditHydrantsLayerId(value) {
      try {
        const payload = {
          layer_id: value,
        };
        await this.editHydrantsLayerId(payload);
      } catch {
        this.editingHydrantsLayerId = JSON.parse(JSON.stringify(this.hydrantsLayerId));
      }
    },
    async init() {
      await this.getModulesMapping();
      await Promise.all([
        this.getDataSourceForm(this.hydrantsInspectionsDataSourceName),
        this.getFeaturesLayer(this.hydrantsInspectionsLayerId),
        this.getLayerMetadata({ dataSource: this.hydrantsInspectionsDataSourceName }),
        this.getHydrantsAvailableLayers(),
        this.getHydrantsLayerId(),
      ]);
      this.editingHydrantsLayerId = JSON.parse(JSON.stringify(this.hydrantsLayerId));
      this.isFormFetched = true;
    },
    async saveCard() {
      await this.editLayer({
        layer_type: 'features_layers',
        layer_id: this.hydrantsInspectionsLayerId,
        body: {
          form_schema: {
            elements: this.currentLayerSchema,
          },
        },
      });
    },
    async openSymbolizationDialog() {
      this.isStyleDialogOpening = true;
      await this.getFeaturesLayer(this.hydrantsInspectionsLayerId);
      const l = this.hydrantsInspectionsLayer;
      this.currentLayerStyleEditGeometryType = l.geometry_type;
      this.currentLayerStyleEdit = JSON.parse(
        JSON.stringify({
          ...l,
          style: l.style_web,
          layer_id: l.id,
        })
      );
      const value = await Promise.resolve(this.getLayerMetadata({ dataSource: l.data_source_name }));
      this.currentLayerStyleEditAttributes = value.metadata.attributes_schema.attributes
        .filter(a => a.data_type.name !== 'geometric')
        .map(a => {
          const isRelational = Boolean(a.relation);
          const dataType = a.data_type;
          const isDate = ['datetime', 'date', 'time'].includes(dataType.name);
          return {
            isRelational,
            valueLabel: isRelational ? `mvt_rel_${a.name}` : a.name,
            valueMvt: isDate ? `mvt_int_${a.name}` : a.name,
            value: a.name,
            text: a.verbose_name,
            dataType,
          };
        });
      this.isStyleDialogOpening = false;
      this.isStyleDialogLoaded = true;
      this.isStyleDialogVisible = true;
    },
    async setLayerStyle(e) {
      try {
        this.isStyleButtonLoading = true;
        const { layer_id, style } = e;
        await this.editLayer({
          layer_id,
          layer_type: 'features_layers',
          body: {
            style_web: style,
          },
        });
      } finally {
        this.isStyleButtonLoading = false;
        this.isStyleDialogLoaded = false;
        this.isStyleDialogVisible = false;
      }
    },
  },
  watch: {
    isModuleEnabled(nV) {
      if (nV) {
        this.isFormFetched = false;
        this.init();
      }
    },
  },
  created() {
    if (this.isModuleEnabled) {
      this.init();
    } else {
      this.isFormFetched = true;
    }
  },
};
</script>
